import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Select, Tag } from 'antd';
import { useLoyaltyBannerStore } from '../../../contexts/mobx';
import { LoyaltyClubForPicker } from '../../../stores/loyalty-banner';
import style from './LoyaltyClubPicker.module.scss';

const { Option } = Select;

interface LoyaltyClubPickerProps {
    value: number | null;
    onChange: (value: number | null) => void;
    onClubSelected?: (club: LoyaltyClubForPicker) => void;
}

export const LoyaltyClubPicker = observer(
    ({ value, onChange, onClubSelected }: LoyaltyClubPickerProps) => {
        const loyaltyBannerStore = useLoyaltyBannerStore();

        useEffect(() => {
            if (loyaltyBannerStore.loyaltyClubs.length === 0) {
                loyaltyBannerStore.fetchLoyaltyClubs();
            }
        }, [loyaltyBannerStore]);

        const getTranslatedName = (club: LoyaltyClubForPicker) => {
            const enTranslation = club.translations.find(
                (t) => t.language === 'en'
            );
            return enTranslation?.name ?? club.name;
        };

        const formatDate = (dateStr: string) => {
            return new Date(dateStr).toLocaleDateString('en-CA', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        };

        return (
            <Select
                value={value ?? undefined}
                onChange={(val) => {
                    onChange(val ?? null);
                    if (val && onClubSelected) {
                        const club = loyaltyBannerStore.loyaltyClubs.find(
                            (c) => c.id === val
                        );
                        if (club) {
                            onClubSelected(club);
                        }
                    }
                }}
                placeholder="Select a loyalty club"
                allowClear
                showSearch
                loading={loyaltyBannerStore.isFetchingLoyaltyClubs}
                filterOption={(input, option) => {
                    const club = loyaltyBannerStore.loyaltyClubs.find(
                        (c) => c.id === option?.value
                    );
                    if (!club) return false;
                    return getTranslatedName(club)
                        .toLowerCase()
                        .includes(input.toLowerCase());
                }}
                className={style.club_select}
            >
                {loyaltyBannerStore.loyaltyClubs.map((club) => {
                    const enTranslation = club.translations.find(
                        (t) => t.language === 'en'
                    );
                    const image = enTranslation?.image ?? null;

                    return (
                        <Option key={club.id} value={club.id}>
                            <div className={style.club_option}>
                                {image && (
                                    <img
                                        src={image}
                                        alt=""
                                        className={style.club_image}
                                    />
                                )}
                                <span>{getTranslatedName(club)}</span>
                                {club.externalProvider && (
                                    <Tag
                                        color="blue"
                                        className={style.club_provider_tag}
                                    >
                                        {club.externalProvider}
                                    </Tag>
                                )}
                                <span className={style.club_date}>
                                    {club.endDate
                                        ? formatDate(club.endDate)
                                        : formatDate(
                                              club.registrationStartDate
                                          )}
                                </span>
                            </div>
                        </Option>
                    );
                })}
            </Select>
        );
    }
);
